import { Injectable } from '@angular/core';
import {
  ConversationState,
  DraftConversation,
  SyncedConversation,
  RestoredConversation,
  OrphanedConversation,
  OrphanReason,
  isDraft,
  isSynced,
  isRestored,
  isOrphaned
} from '../../models/conversation-state';

/**
 * Conversation State Machine - Enforces Valid State Transitions
 *
 * Design Principle: Single Source of Truth for state transitions.
 * All state changes MUST go through this service.
 * Invalid transitions throw errors at runtime (but TypeScript catches most at compile time).
 *
 * Valid Transitions:
 * - DRAFT → SYNCED (backend confirms)
 * - RESTORED → SYNCED (backend has conversation)
 * - RESTORED → ORPHANED (backend missing)
 * - SYNCED → ORPHANED (backend deletion detected)
 *
 * Invalid Transitions (will throw):
 * - DRAFT → ORPHANED (draft never had backend presence)
 * - ORPHANED → DRAFT (cannot un-orphan)
 * - Any state → RESTORED (only created via restoration)
 */
@Injectable({ providedIn: 'root' })
export class ConversationStateMachine {

  /**
   * Create a new DRAFT conversation
   * This is the ONLY way to create a new conversation state
   */
  createDraft(id: string): DraftConversation {
    return {
      kind: 'DRAFT',
      id,
      createdAt: new Date(),
      messageCount: 0
    };
  }

  /**
   * Restore conversation from IndexedDB
   * This is the ONLY way to create a RESTORED state
   */
  restoreFromStorage(id: string, messageCount: number): RestoredConversation {
    return {
      kind: 'RESTORED',
      id,
      messageCount,
      restoredAt: new Date(),
      needsSyncCheck: true  // Type system forces handling
    };
  }

  /**
   * Transition: DRAFT → SYNCED
   * Called when backend confirms conversation creation
   */
  markSyncedFromDraft(state: ConversationState, backendMessageCount: number): SyncedConversation {
    if (!isDraft(state)) {
      throw new Error(`INVALID TRANSITION: Can only sync DRAFT conversations, got ${state.kind}`);
    }

    return {
      kind: 'SYNCED',
      id: state.id,
      lastSyncedAt: new Date(),
      backendMessageCount
    };
  }

  /**
   * Transition: RESTORED → SYNCED
   * Called when backend check confirms conversation exists
   */
  markSyncedFromRestored(state: ConversationState, backendMessageCount: number): SyncedConversation {
    if (!isRestored(state)) {
      throw new Error(`INVALID TRANSITION: Can only sync RESTORED conversations, got ${state.kind}`);
    }

    return {
      kind: 'SYNCED',
      id: state.id,
      lastSyncedAt: new Date(),
      backendMessageCount
    };
  }

  /**
   * Transition: RESTORED → ORPHANED
   * Called when backend check returns 404
   */
  markOrphanedFromRestored(state: ConversationState, reason: OrphanReason): OrphanedConversation {
    if (!isRestored(state)) {
      throw new Error(`INVALID TRANSITION: Can only orphan RESTORED conversations, got ${state.kind}`);
    }

    return {
      kind: 'ORPHANED',
      id: state.id,
      messageCount: state.messageCount,
      reason,
      detectedAt: new Date()
    };
  }

  /**
   * Transition: SYNCED → ORPHANED
   * Called when backend deletion is detected (rare case)
   */
  markOrphanedFromSynced(state: ConversationState, reason: OrphanReason): OrphanedConversation {
    if (!isSynced(state)) {
      throw new Error(`INVALID TRANSITION: Can only orphan SYNCED conversations, got ${state.kind}`);
    }

    // We don't have messageCount in SYNCED state, use backend count
    return {
      kind: 'ORPHANED',
      id: state.id,
      messageCount: state.backendMessageCount,
      reason,
      detectedAt: new Date()
    };
  }

  /**
   * Update message count in DRAFT state
   */
  updateDraftMessageCount(state: ConversationState, messageCount: number): DraftConversation {
    if (!isDraft(state)) {
      throw new Error(`INVALID OPERATION: Can only update message count in DRAFT state, got ${state.kind}`);
    }

    return {
      ...state,
      messageCount
    };
  }

  /**
   * Update sync timestamp in SYNCED state
   */
  updateSyncTimestamp(state: ConversationState, backendMessageCount: number): SyncedConversation {
    if (!isSynced(state)) {
      throw new Error(`INVALID OPERATION: Can only update sync in SYNCED state, got ${state.kind}`);
    }

    return {
      ...state,
      lastSyncedAt: new Date(),
      backendMessageCount
    };
  }

  /**
   * Convert ORPHANED back to DRAFT (when user chooses to upload)
   * This is a special case that creates a new logical conversation
   */
  resurrectOrphaned(state: ConversationState): DraftConversation {
    if (!isOrphaned(state)) {
      throw new Error(`INVALID OPERATION: Can only resurrect ORPHANED conversations, got ${state.kind}`);
    }

    return {
      kind: 'DRAFT',
      id: state.id,
      createdAt: new Date(),
      messageCount: state.messageCount
    };
  }

  /**
   * Validate state transition (for debugging)
   */
  canTransition(from: ConversationState['kind'], to: ConversationState['kind']): boolean {
    const validTransitions: Record<ConversationState['kind'], ConversationState['kind'][]> = {
      'DRAFT': ['SYNCED'],
      'SYNCED': ['ORPHANED'],
      'RESTORED': ['SYNCED', 'ORPHANED'],
      'ORPHANED': ['DRAFT']  // Special case: resurrection
    };

    return validTransitions[from]?.includes(to) ?? false;
  }

  /**
   * Get human-readable state description
   */
  getStateDescription(state: ConversationState): string {
    switch (state.kind) {
      case 'DRAFT':
        return 'Local draft (not yet synced)';

      case 'SYNCED':
        return `Synced with backend (${state.backendMessageCount} messages)`;

      case 'RESTORED':
        return 'Restored from local storage (checking backend...)';

      case 'ORPHANED':
        return `Local only (${this.getOrphanReasonDescription(state.reason)})`;

      default:
        const _exhaustive: never = state;
        return 'Unknown state';
    }
  }

  /**
   * Get human-readable orphan reason
   */
  private getOrphanReasonDescription(reason: OrphanReason): string {
    switch (reason) {
      case 'backend_expired':
        return 'backend data expired';
      case 'backend_deleted':
        return 'conversation deleted';
      case 'never_synced':
        return 'never uploaded';
      case 'sync_failed':
        return 'sync failed';
      default:
        const _exhaustive: never = reason;
        return 'unknown reason';
    }
  }
}
